import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import WidgetHeader from '@/components/common/WidgetHeader';
import AsOfDatePicker from '@/components/date/AsOfDatePicker';
import DateRange from '@/components/date/DateRange';
import BalanceSheetDetails from '@/modules/balanceSheet/balanceSheetDetails';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import { dashboardMobileWidthValue } from '@/constants';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import { ViewMode } from '@/constants/enum';
import { useControlledDates } from '@/hooks/useControlledDates';
import { useBalanceSheetData } from '@/hooks/useBalanceSheetData';
import { useLocalization } from '@/hooks/useLocalization';
import { usePageDefaults } from '@/hooks/usePageDefaults';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { StandardWidgetProps } from '@/models/WidgetModels';
import {
  selectExcludedAccounts,
  selectExcludedAccountTypes,
  selectExcludedCategories,
  selectExcludedInstitutions,
} from '@/store/filterSlice';
import { WARootState } from '@/store/store';
import { ContentContainer } from '../contentContainer';

const { startDate: initialPageStartDate, endDate: initialPageEndDate } =
  getDateRangeFromFilter(DataRangeFilterEnum.OpenYear);

/**
 * Full-width balance sheet experience (charts, table, summary, historical)
 * matching the body of `/Investor/BalanceSheet`, for embeddable widget hosts.
 */
export default function BalanceSheetChartWidget({
  isWidget = true,
  onDataLoaded,
  title,
  startDate: propStartDate,
  endDate: propEndDate,
  container = 'widget',
}: StandardWidgetProps) {
  const { t } = useLocalization();
  const widgetTitle = title ?? t('balanceSheet');
  const { ref: widgetWidthRef, isBelow: isWidgetBelowNarrowDateField } =
    useWidgetWidthIsBelow(isWidget, dashboardMobileWidthValue);
  const widgetNarrowDateField = isWidget && isWidgetBelowNarrowDateField;

  const { defaultView: firmDefaultView } = usePageDefaults(
    '/BalanceSheet',
    'balanceSheet',
  );
  const firmDefaultViewMode = Object.values(ViewMode).find(
    (viewMode) => viewMode === firmDefaultView,
  );

  const [dateFilter, setDateFilter] = useState<DataRangeFilterEnum>(
    DataRangeFilterEnum.OpenYear,
  );
  const [useSingleDate, setUseSingleDate] = useState(
    firmDefaultViewMode !== ViewMode.Historical,
  );

  const { startDate, endDate, setStartDate, setEndDate } = useControlledDates({
    propStartDate,
    propEndDate,
    isWidget,
    initialWidgetStartDate: initialPageStartDate,
    initialWidgetEndDate: initialPageEndDate,
  });

  const userId = useSelector(
    (state: WARootState) => state.appView.UserInfo?.UserId,
  );
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );

  const excludedAccounts = useSelector((state: WARootState) =>
    selectExcludedAccounts(state, userId, currentRole),
  );
  const excludedCategories = useSelector((state: WARootState) =>
    selectExcludedCategories(state, userId, currentRole),
  );
  const excludedAccountTypes = useSelector((state: WARootState) =>
    selectExcludedAccountTypes(state, userId, currentRole),
  );
  const excludedInstitutions = useSelector((state: WARootState) =>
    selectExcludedInstitutions(state, userId, currentRole),
  );

  const accountFilters = useMemo(() => {
    const hasFilters =
      excludedAccounts.length > 0 ||
      excludedCategories.length > 0 ||
      excludedAccountTypes.length > 0 ||
      excludedInstitutions.length > 0;

    if (!hasFilters) {
      return undefined;
    }

    return {
      excludedAccounts:
        excludedAccounts.length > 0 ? excludedAccounts : undefined,
      excludedCategories:
        excludedCategories.length > 0 ? excludedCategories : undefined,
      excludedAccountTypes:
        excludedAccountTypes.length > 0 ? excludedAccountTypes : undefined,
      excludedInstitutions:
        excludedInstitutions.length > 0 ? excludedInstitutions : undefined,
    };
  }, [
    excludedAccounts,
    excludedCategories,
    excludedAccountTypes,
    excludedInstitutions,
  ]);

  const { balanceData, isFetching } = useBalanceSheetData(
    endDate,
    undefined,
    accountFilters,
  );

  useEffect(() => {
    if (!isFetching) {
      onDataLoaded?.();
    }
  }, [isFetching, onDataLoaded]);

  const handleCustomDateRangeChange = (
    start: string,
    end: string,
    _shortcutLabel?: string,
    shortcutFilter?: DataRangeFilterEnum,
  ) => {
    setStartDate(start);
    setEndDate(end);
    setDateFilter(shortcutFilter ?? DataRangeFilterEnum.Custom);
  };

  const datePicker = useSingleDate ? (
    <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
      <AsOfDatePicker
        date={endDate}
        onChange={(date) => {
          setEndDate(date);
          setDateFilter(DataRangeFilterEnum.Custom);
        }}
        useMobilePrimaryIconButton={widgetNarrowDateField}
        useWidgetNarrowIconField={widgetNarrowDateField}
      />
    </Box>
  ) : (
    <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
      <DateRange
        startDate={startDate}
        endDate={endDate}
        onChange={handleCustomDateRangeChange}
        useMobilePrimaryIconButton={widgetNarrowDateField}
        useWidgetNarrowIconField={widgetNarrowDateField}
      />
    </Box>
  );

  return (
    <ContentContainer
      container={container}
      ref={widgetWidthRef}
      sx={{
        paddingX: 3,
        paddingTop: 3,
        paddingBottom: 5,
        gap: 4,
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        maxHeight: isWidget ? '600px' : 'none',
        height: isWidget ? '100%' : 'auto',
        minHeight: 0,
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        '@media print': {
          paddingX: 0,
          paddingTop: 0,
          paddingBottom: 0,
          gap: 0,
        },
      }}
    >
      <WidgetHeader
        title={widgetTitle}
        rightContent={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            {datePicker}
          </Box>
        }
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: isWidget ? 'auto' : 'visible',
          overflowX: 'hidden',
          pr: isWidget ? 0.5 : 0,
        }}
      >
        <BalanceSheetDetails
          balanceData={balanceData}
          dateFilter={dateFilter}
          startDate={startDate}
          endDate={endDate}
          isLoading={isFetching}
          firmDefaultView={firmDefaultViewMode}
          excludedAccounts={excludedAccounts}
          excludedCategories={excludedCategories}
          excludedAccountTypes={excludedAccountTypes}
          excludedInstitutions={excludedInstitutions}
          isWidget={isWidget}
          disablePaperFrame
          onViewModeChange={(viewMode) => {
            setUseSingleDate(viewMode !== ViewMode.Historical);
          }}
        />
      </Box>
    </ContentContainer>
  );
}
